import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { api, RON, track } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const STEPS = [
  "Completezi formularul",
  "Verifici datele",
  "Plătești 10 lei",
  "Primești documentul",
];

export default function DocumentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);

  useSEO(doc?.seo_title, doc?.seo_description);

  useEffect(() => {
    setDoc(null);
    setError(false);
    api
      .get(`/documents/${slug}`)
      .then((r) => {
        setDoc(r.data);
        track("document_view", { slug });
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">
          Documentul nu a fost găsit
        </h1>
        <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-blue-600 hover:bg-blue-700">
          Înapoi la pagina principală
        </Button>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="flex items-center justify-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const startFlow = () => {
    track("form_started", { slug });
    navigate(`/documente/${slug}/genereaza`);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-12 sm:py-16 pb-28">
      <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100 rounded-full font-semibold">
        {doc.category}
      </Badge>
      <div className="flex items-start gap-4">
        <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 text-white shrink-0">
          <FileText className="w-7 h-7" />
        </span>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-tight">
            {doc.title}
          </h1>
          <p className="mt-4 text-base text-slate-600 dark:text-slate-300 leading-relaxed">
            {doc.long_description || doc.description}
          </p>
        </div>
      </div>

      {/* Cum funcționează */}
      <div className="mt-10 p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
        <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-4">
          Cum funcționează
        </h2>
        <ol className="space-y-3">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-sm font-bold shrink-0">
                {i + 1}
              </span>
              <span className="text-slate-700 dark:text-slate-200">{s}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Preț + CTA */}
      <div className="mt-8 p-6 rounded-2xl bg-slate-900 dark:bg-slate-800 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <p className="text-sm text-slate-300">Preț document</p>
          <p className="font-heading text-3xl font-extrabold">{RON(doc.price)}</p>
          <p className="text-xs text-slate-400">Plată unică. Fără abonament.</p>
        </div>
        <Button
          data-testid="start-generate-btn"
          onClick={startFlow}
          size="lg"
          className="w-full sm:w-auto rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 px-8"
        >
          Generează documentul
          <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
      </div>

      {/* FAQ */}
      {doc.faq?.length > 0 && (
        <div className="mt-12">
          <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-4">
            Întrebări frecvente
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {doc.faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-medium text-slate-800 dark:text-slate-100">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 dark:text-slate-300">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      <p className="mt-10 text-xs text-slate-400 leading-relaxed p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
        {doc.disclaimer}
      </p>

      {/* Sticky mobile CTA */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 p-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800">
        <Button
          data-testid="sticky-generate-btn"
          onClick={startFlow}
          className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 text-base"
        >
          Generează – {RON(doc.price)}
        </Button>
      </div>
    </div>
  );
}
